import {ChatMessage, ChatMessageType, ParticipantKey} from "./types";
import {Box, Card, Typography} from "@mui/material";
import {
  BORDER_RADIUS,
  BORDER_RADIUS_MEDIUM,
  CARD_HORIZONTAL_PADDING,
  PAGE_FRAGMENT_SM_WIDTH,
  PD_MD,
  PD_SM,
  PD_XSM,
  PD_XXSM,
  SZ_SM,
  SZ_XSM
} from "@/shared/dimens";
import React, {Component} from "react";
import {colorHighlight, translucentWhite, white} from "@/shared/colors";
import {DateUtil, FORMAT_SHOW_YEAR} from "../date_util";
import {ChatItemViewContainer, ChatItemViewFrom, ChatItemViewType, ChatItemViewUnavailable, ChatUtil} from "./chat";
import {AVATAR_SM_STYLE} from "@/shared/constants";
import {ExpandMore} from "@mui/icons-material";
import {ColorValue} from "@/shared/types";
import {BaseApp} from "@/shared/BaseApp";
import {StyledBoxColumn, StyledBoxRow} from "@/shared/StyledComponents";
import {CALL_CHANNEL_METADATAS_KTM, CallChannel} from "../calls/types";
import Markdown from "react-markdown";

const PROFILE_IMAGE_STYLE = {...AVATAR_SM_STYLE, flexShrink: 0};

export enum ChatViewContext {
  THREAD,
  PREVIEW,
  QUOTE,
}

export type ChatItem = {
  prevMessage?: ChatMessage,
  message: ChatMessage,
  nextMessage?: ChatMessage,
  viewContext: ChatViewContext,
}

type ChatItemViewProps = {
  myParticipantKey: ParticipantKey,
  item: ChatItem,
  color?: "primary" | "secondary",
  meColor?: ColorValue,
  otherColor?: ColorValue,
  chatViewListener?: ChatViewListener,
  alignment?: "left" | "auto",
}

type ChatItemViewState = {
  hover?: boolean,
}

export interface ChatViewListener {
  onChatViewOptionsClicked(message: ChatMessage): void,
}

export class ChatItemView extends Component<ChatItemViewProps, ChatItemViewState> {

  private readonly theme = BaseApp.CONTEXT.getAppConfig().theme;
  private readonly themeColor = BaseApp.CONTEXT.getAppConfig().themeColor || this.theme.palette.primary.main;

  constructor(props: ChatItemViewProps, context: any) {
    super(props, context);
    this.state = {};
  }

  render() {
    const prevMessage = this.props.item.prevMessage;
    const message = this.props.item.message;
    const nextMessage = this.props.item.nextMessage;
    const viewContext = this.props.item.viewContext;
    // const meColor = this.props.meColor || new ColorValue(this.theme.palette[this.props.color || "primary"].contrastText, this.theme.palette[this.props.color || "primary"].main);
    const meColor = this.props.meColor || new ColorValue(white, this.props.color === "secondary" ? this.theme.palette.primary.main : this.themeColor);
    const otherColor = this.props.otherColor || new ColorValue(this.theme.palette.primary.main, this.theme.palette.primary.contrastText);
    const viewType = ChatUtil.getItemViewType(message, this.props.myParticipantKey.id);
    const messageView = this.renderMessageType(message, viewType);
    const isLeftAligned = this.props?.alignment === "left";
    let itemView;
    switch (viewType.container) {
      case ChatItemViewContainer.MESSAGE: {
        switch (viewContext) {
          case ChatViewContext.THREAD:
            let unavailable = false;
            let hasMoreOptions = this.hasMoreOptions(message);
            switch (viewType.from) {
              case ChatItemViewFrom.ME:
                switch (viewType.unavailable) {
                  default:
                  case ChatItemViewUnavailable.NONE:
                    const continueMessage = nextMessage?.from?.id === message?.from?.id;
                    let profileImageUrl = !continueMessage ? this.getProfileImageUrl(message, nextMessage) : null;
                    itemView = (
                      <Box style={{
                        display: "flex",
                        justifyContent: isLeftAligned ? "flex-start" : "flex-end",
                        alignItems: isLeftAligned ? "flex-end" : "flex-start",
                        marginBottom: continueMessage ? -PD_SM : 0,
                        gap: PD_MD,
                      }}>
                        {isLeftAligned
                          ? profileImageUrl
                            ? <Card style={PROFILE_IMAGE_STYLE}>
                              <img src={profileImageUrl} style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                            </Card>
                            : <Box style={PROFILE_IMAGE_STYLE}/>
                          : null}
                        <Card
                          style={{
                            display: "flex",
                            maxWidth: PAGE_FRAGMENT_SM_WIDTH,
                            background: meColor.background,
                            color: meColor.color,
                            borderRadius: BORDER_RADIUS_MEDIUM,
                            borderBottomRightRadius: !isLeftAligned ? BORDER_RADIUS : BORDER_RADIUS_MEDIUM,
                            borderBottomLeftRadius: !isLeftAligned ? BORDER_RADIUS_MEDIUM : BORDER_RADIUS,
                            minHeight: SZ_SM,
                            marginRight: !isLeftAligned ? CARD_HORIZONTAL_PADDING : 0,
                            position: "relative",
                            cursor: hasMoreOptions ? "pointer" : undefined,
                          }}
                          onMouseEnter={() => this.setState({hover: true})}
                          onMouseLeave={() => this.setState({hover: false})}
                          onClick={() => {
                            if (hasMoreOptions) {
                              this.props.chatViewListener?.onChatViewOptionsClicked(this.props.item.message);
                            }
                          }}>
                          {messageView}
                          {hasMoreOptions
                            ? <div style={{
                              visibility: this.state.hover ? "visible" : "hidden",
                              background: translucentWhite,
                              color: "black",
                              position: "absolute",
                              top: 0,
                              right: 0,
                              width: SZ_XSM,
                              height: SZ_XSM
                            }}>
                              <ExpandMore style={{padding: PD_XXSM}}/>
                            </div>
                            : null}
                        </Card>
                      </Box>
                    );
                    break;
                  case ChatItemViewUnavailable.UNKNOWN:
                  case ChatItemViewUnavailable.EXPIRED:
                  case ChatItemViewUnavailable.DELETED:
                    unavailable = true;
                    itemView = (
                      <Box display="flex" flexDirection="row" justifyContent="flex-end">
                        <Card style={{
                          display: "flex",
                          maxWidth: PAGE_FRAGMENT_SM_WIDTH,
                          background: meColor.background,
                          color: meColor.color,
                          borderRadius: BORDER_RADIUS_MEDIUM,
                          borderBottomRightRadius: !isLeftAligned ? BORDER_RADIUS : BORDER_RADIUS_MEDIUM,
                          borderBottomLeftRadius: !isLeftAligned ? BORDER_RADIUS_MEDIUM : BORDER_RADIUS,
                          minHeight: SZ_SM,
                          marginRight: CARD_HORIZONTAL_PADDING,
                          opacity: "0.25",
                        }}>
                          {messageView}
                        </Card>
                      </Box>
                    );
                    break;
                }
                break;
              case ChatItemViewFrom.OTHER:
                switch (viewType.unavailable) {
                  default:
                  case ChatItemViewUnavailable.NONE:
                    const continueMessage = nextMessage?.from?.id === message?.from?.id;
                    let profileImageUrl = !continueMessage ? this.getProfileImageUrl(message, nextMessage) : null;
                    itemView = (
                      <Box
                        style={{
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "flex-end",
                          marginBottom: continueMessage ? -PD_SM : 0,
                          gap: PD_MD,
                        }}>
                        {
                          profileImageUrl ?
                            <Card style={PROFILE_IMAGE_STYLE}>
                              <img src={profileImageUrl}
                                   style={{width: "100%", height: "100%", objectFit: "cover"}}/>
                            </Card> :
                            <Box style={PROFILE_IMAGE_STYLE}/>
                        }
                        <Card
                          style={{
                            display: "flex",
                            maxWidth: PAGE_FRAGMENT_SM_WIDTH,
                            background: otherColor.background,
                            color: otherColor.color,
                            borderRadius: BORDER_RADIUS_MEDIUM,
                            borderBottomLeftRadius: BORDER_RADIUS,
                            minHeight: SZ_SM,
                            position: "relative",
                            cursor: hasMoreOptions ? "pointer" : undefined,
                          }}
                          onMouseEnter={() => this.setState({hover: true})}
                          onMouseLeave={() => this.setState({hover: false})}
                          onClick={() => {
                            if (hasMoreOptions) {
                              this.props.chatViewListener?.onChatViewOptionsClicked(this.props.item.message);
                            }
                          }}>
                          {messageView}
                          {hasMoreOptions
                            ? <div style={{
                              visibility: this.state.hover ? "visible" : "hidden",
                              background: translucentWhite,
                              color: "black",
                              position: "absolute",
                              top: 0,
                              right: 0,
                              width: SZ_XSM,
                              height: SZ_XSM
                            }} onClick={(event) => {
                              event.stopPropagation();
                              this.props.chatViewListener?.onChatViewOptionsClicked(this.props.item.message)
                            }}>
                              <ExpandMore style={{padding: PD_XXSM}}/>
                            </div>
                            : null}
                        </Card>
                      </Box>
                    );
                    break;
                  case ChatItemViewUnavailable.UNKNOWN:
                  case ChatItemViewUnavailable.EXPIRED:
                  case ChatItemViewUnavailable.DELETED:
                    unavailable = true;
                    itemView = (
                      <Box display="flex" flexDirection="row" justifyContent="flex-start">
                        <Card style={{
                          display: "flex",
                          maxWidth: PAGE_FRAGMENT_SM_WIDTH,
                          background: otherColor.background,
                          color: otherColor.color,
                          borderRadius: BORDER_RADIUS_MEDIUM,
                          borderBottomLeftRadius: BORDER_RADIUS,
                          opacity: "0.25",
                          minHeight: `${SZ_SM}`,
                          margin: `${PD_XSM}`,
                          alignSelf: "flex-start"
                        }}>
                          {messageView}
                        </Card>
                      </Box>
                    );
                    break;
                }
                break;
              default:
                throw new Error("Unknown view type: " + viewType);
            }
            if (!unavailable) {
              // View
              // quoteView = itemView.findViewById(R.id.quote);
              // quoteViewHolder = new QuoteViewHolder(quoteView, quoteView.findViewById(R.id.quote_attribution_icon), quoteView.findViewById(R.id.quote_attribution_text), quoteView.findViewById(R.id.quote_chat_container));
            }
            break;
          default:
            throw new Error("Invalid or unknown context type: " + viewContext);
        }
      }
        break;
      case ChatItemViewContainer.TRANSPARENT: {
        switch (viewContext) {
          case ChatViewContext.THREAD:
            switch (viewType.unavailable) {
              case ChatItemViewUnavailable.NONE:
                itemView = (
                  <Box display="flex" flexDirection="row" justifyContent="center">
                    {messageView}
                  </Box>
                );
                break;
              case ChatItemViewUnavailable.UNKNOWN:
              case ChatItemViewUnavailable.EXPIRED:
              case ChatItemViewUnavailable.DELETED:
              default:
                itemView = (
                  <Box display="flex" flexDirection="row" justifyContent="flex-start">
                    {messageView}
                  </Box>
                );
                break;
            }
            break;
          default:
            throw new Error("Invalid or unknown context type: " + viewContext);
        }
      }
        break;
      case ChatItemViewContainer.MESSAGE_TRANSPARENT: {
        // TODO: Handle
      }
        break;
      case ChatItemViewContainer.MESSAGE_STATE: {
        // TODO: Handle
      }
        break;
    }
    return itemView;
  }

  private hasMoreOptions(message: ChatMessage): boolean {
    switch (message.type) {
      case ChatMessageType.TEXT:
        return true;
    }
    return false;
  }

  private getDisplayName(prevMessage: ChatMessage, message: ChatMessage): string {
    if (!message.isUserGenerated(true) || (prevMessage.isUserGenerated(true) && message.from.id === prevMessage.from.id)) {
      return null;
    } else {
      return message.fromMetadata()?.title;
    }
  }

  private getProfileImageUrl(message: ChatMessage, nextMessage?: ChatMessage): string {
    if (!message.isUserGenerated(true) || (nextMessage?.isUserGenerated(true) && message.from === nextMessage?.from)) {
      return null;
    } else {
      return message.fromMetadata()?.profilePhoto;
    }
  }

  private renderMessageType(message: ChatMessage, viewType: ChatItemViewType) {
    if (viewType.unavailable !== ChatItemViewUnavailable.NONE) {
      // Render Unavailable
      return null;
    }
    switch (message.type) {
      case ChatMessageType.META:
        return this.renderMeta(message, viewType);
      case ChatMessageType.TEXT:
        return this.renderText(message, viewType);
      case ChatMessageType.CALL:
        return this.renderCall(message, viewType);
    }
    return null;
  }

  private renderUnavailableMeta(message: ChatMessage, viewType: ChatItemViewType) {
    return (
      <Box>

      </Box>
    );
  }

  private renderUnavailableText(message: ChatMessage, viewType: ChatItemViewType) {
    return (
      <Box>

      </Box>
    );
  }

  private renderMeta(message: ChatMessage, viewType: ChatItemViewType) {
    return (
      <Card style={{
        display: "flex",
        background: `${colorHighlight}`,
        margin: PD_XSM,
        alignSelf: "center"
      }}>
        <Typography
          variant="caption"
          style={{
            color: "black",
            alignSelf: "center",
            paddingTop: PD_XXSM,
            paddingBottom: PD_XXSM,
            paddingLeft: PD_XSM,
            paddingRight: PD_XSM,
          }}>{DateUtil.formatDateTime(Number.parseInt(message.meta.data), FORMAT_SHOW_YEAR)}</Typography>
      </Card>
    );
  }

  private renderText(message: ChatMessage, viewType: ChatItemViewType) {
    return <Typography
      component="div"
      style={{
        alignSelf: "center",
        paddingTop: PD_XSM,
        paddingBottom: PD_XSM,
        paddingLeft: PD_MD,
        paddingRight: PD_MD,
      }}>
      <Markdown>
        {message.text}
      </Markdown>
    </Typography>;
  }

  private renderCall(message: ChatMessage, viewType: ChatItemViewType) {
    if (!message.callLog) {
      return <Typography>Call log deleted</Typography>;
    }
    const channelMetadata = CALL_CHANNEL_METADATAS_KTM.findMetadata(message.callLog.channel || CallChannel.AUDIO);
    return (
      <StyledBoxRow style={{alignItems: "center", padding: PD_SM}}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 48,
          height: 48,
          background: "rgba(128, 128, 128, 0.25)",
          borderRadius: 24
        }}>
          <channelMetadata.iconType/>
        </div>
        <StyledBoxColumn style={{gap: 0, padding: PD_SM}}>
          <Typography>
            {channelMetadata.text}
          </Typography>
          <Typography variant={"caption"} style={{opacity: 0.5}}>No answer</Typography>
        </StyledBoxColumn>
      </StyledBoxRow>
    );
  }
}
